import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/components/auth/AuthProvider';
import { NutritionDataService } from '@/services/nutrition/NutritionDataService';
import { NutritionDataType, NutritionQueryData } from '@/types/nutrition';

interface UseNutritionDataOptions {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
}

export function useNutritionData<T extends NutritionDataType>(
  type: T,
  options: UseNutritionDataOptions = {},
  params?: { date?: string; startDate?: string; endDate?: string }
) {
  const { user } = useAuth();
  const { enabled = true, refetchInterval, staleTime = 5 * 60 * 1000 } = options;

  return useQuery<NutritionQueryData[T]>({
    queryKey: [type, user?.id, params?.date, params?.startDate, params?.endDate],
    queryFn: () => {
      if (!user) throw new Error('User not authenticated');
      return NutritionDataService.getData(type, user.id, params);
    },
    enabled: enabled && !!user,
    refetchInterval,
    staleTime,
  });
}

// Convenience hooks for common data types
export const useDailySummary = (options?: UseNutritionDataOptions) => 
  useNutritionData('daily-summary', options);

export const useTodaysMeals = (options?: UseNutritionDataOptions) => 
  useNutritionData('meals-today', options);

export const useNutritionGoals = (options?: UseNutritionDataOptions) => 
  useNutritionData('goals', options);

export const useNutritionInsights = (options?: UseNutritionDataOptions) => 
  useNutritionData('insights', options);

export const usePerformanceCorrelations = (options?: UseNutritionDataOptions) => 
  useNutritionData('performance-correlations', options);

export const useNetCalories = (options?: UseNutritionDataOptions) => 
  useNutritionData('net-calories', options);

export const useWeeklyConsistency = (options?: UseNutritionDataOptions) => 
  useNutritionData('weekly-consistency', options);